import { helper } from '@core/mixins/helper.js';
import { checkHasRole } from '@core/utils/roleAccess';
import axios from 'axios';
import toastHelper from '@core/helpers/toast.helper.js';
import i18n from '@/libs/i18n';
import serviceDefaultConfig from './serviceDefaultConfig';
import WebhookService from './modules/webhooks.service';
import AuthService from './modules/auth.service';
import ConsignmentService from './modules/consignment.service';
import ConstantService from './modules/constant.service';
import CustomerService from './modules/customer.service';
import DepartmentService from './modules/department.service';
import OfferService from './modules/offer.service';
import PickupService from './modules/pickup.service';
import PackageService from './modules/package.service';
import PostalCodeService from './modules/postalCode.service';
import ProductService from './modules/product.service';
import ProviderService from './modules/provider.service';
import UserService from './modules/user.service';
import WarehouseService from './modules/warehouse.service';
import ApiKeyService from './modules/apiKey.service';
import TaxesService from './modules/taxes.service';
import TaxOfficesService from './modules/taxOffices.service';
import PricingService from './modules/pricing.service';
import ReportsService from './modules/reports.service';
import PaymentService from './modules/payment.service';
import BarcodePoolService from './modules/barcodePool.service';
import IntegrationService from './modules/integration.service';

let isSessionExpiredAlertShown = false;

export default class GlobalService {
  // Will be used by this service for making API calls
  axiosIns = null;

  // serviceConfig <= Will be used by this service
  serviceConfig = { ...serviceDefaultConfig };

  constructor(axiosIns, serviceOverrideConfig) {
    this.axiosIns = axiosIns;
    this.serviceConfig = { ...this.serviceConfig, ...serviceOverrideConfig };

    // Request Interceptor
    this.axiosIns.interceptors.request.use(
      config => {
        if (config.role) {
          if (!checkHasRole(config.role)) {
            const message = `user has no role for ${config.url}`;
            return Promise.reject(message);
          }
        }

        const accessToken = this.authService.getToken();
        const { locale } = i18n;

        if (accessToken) {
          config.headers.Authorization = `${this.serviceConfig.tokenType} ${accessToken}`;
        }

        if (locale) {
          config.headers['Accept-Language'] = locale;
        }

        config.url =
          (!config.url.includes('.json') ? process.env.VUE_APP_API_URL : '') + config.url;

        return config;
      },
      error => Promise.reject(error)
    );

    // Add request/response interceptor
    this.axiosIns.interceptors.response.use(
      response => {
        // refresh token
        if (response.data.token) {
          this.setToken(response.data.token);
        }

        if (response.data && response.data.errors && !response.data.status) {
          let errMsg = 'Error occurred';
          const errs = response.data.errors;
          if (Array.isArray(errs) && errs.length > 0) {
            errMsg = errs[0].message || errMsg;
          } else if (errs && typeof errs === 'object' && errs.message) {
            errMsg = errs.message;
          } else if (typeof errs === 'string') {
            errMsg = errs;
          }

          const error = new Error(errMsg);
          error.data = response.data;
          return Promise.reject(error);
        }

        return Promise.resolve(response.data);
      },
      error => {
        const { response } = error;

        if (response && response.status === 401) {
          const isLoginEndpoint =
            error.config && error.config.url && error.config.url.includes('/api/v1/auth/login');
          if (!isLoginEndpoint) {
            if (!isSessionExpiredAlertShown) {
              isSessionExpiredAlertShown = true;

              toastHelper.showWarningMessage(i18n.t('Session Expired'), i18n.t('Warning'));

              setTimeout(() => {
                helper.methods.setLogout(false);
              }, 2000);
            }
            return new Promise(() => {});
          }
        }

        if (!response) {
          return Promise.reject(error);
        }

        if (response.data && (response.data.code || response.data.detail)) {
          return Promise.reject(response.data);
        }

        return Promise.reject(response.data.errors || response.data);
      }
    );

    // modules
    this.webhookService = new WebhookService(this.axiosIns, this.serviceConfig);
    this.authService = new AuthService(this.axiosIns, this.serviceConfig);
    this.consignmentService = new ConsignmentService(this.axiosIns, this.serviceConfig);
    this.constantService = new ConstantService(this.axiosIns, this.serviceConfig);
    this.customerService = new CustomerService(this.axiosIns, this.serviceConfig);
    this.departmentService = new DepartmentService(this.axiosIns, this.serviceConfig);
    this.offerService = new OfferService(this.axiosIns, this.serviceConfig);
    this.packageService = new PackageService(this.axiosIns, this.serviceConfig);
    this.postalCodeService = new PostalCodeService(this.axiosIns, this.serviceConfig);
    this.productService = new ProductService(this.axiosIns, this.serviceConfig);
    this.providerService = new ProviderService(this.axiosIns, this.serviceConfig);
    this.userService = new UserService(this.axiosIns, this.serviceConfig);
    this.warehouseService = new WarehouseService(this.axiosIns, this.serviceConfig);
    this.apiKeyService = new ApiKeyService(this.axiosIns, this.serviceConfig);
    this.taxesService = new TaxesService(this.axiosIns, this.serviceConfig);
    this.taxOfficesService = new TaxOfficesService(this.axiosIns, this.serviceConfig);
    this.pickupService = new PickupService(this.axiosIns, this.serviceConfig);
    this.pricingService = new PricingService(this.axiosIns, this.serviceConfig);
    this.reportService = new ReportsService(this.axiosIns, this.serviceConfig);
    this.paymentService = new PaymentService(this.axiosIns, this.serviceConfig);
    this.barcodePoolService = new BarcodePoolService(this.axiosIns, this.serviceConfig);
    this.integrationService = new IntegrationService(this.axiosIns, this.serviceConfig);

    this.mergeModuleMethods();
  }

  mergeModuleMethods() {
    // Var olan yapının korunması için yapılmıştır
    // Yeni modüller için tanımlama yapmanıza gerek yoktur
    // Yeni modüller ve yeni yazılan kodlar için önerilen kullanım örneği: "useService.authService.login()"
    const moduleServices = [
      this.webhookService,
      this.authService,
      this.consignmentService,
      this.constantService,
      this.customerService,
      this.departmentService,
      this.offerService,
      this.packageService,
      this.postalCodeService,
      this.productService,
      this.providerService,
      this.userService,
      this.warehouseService,
      this.apiKeyService,
      this.taxesService,
      this.taxOfficesService,
      this.pickupService,
      this.reportService,
      this.paymentService,
      this.barcodePoolService,
      this.integrationService
    ];

    // Merge methods from each module service into GlobalService
    moduleServices.forEach(service => {
      Object.getOwnPropertyNames(Object.getPrototypeOf(service)).forEach(method => {
        if (typeof service[method] === 'function' && method !== 'constructor') {
          if (this[method]) {
            // Farklı modüllerde aynı isimde metod tanımlaması yapılırsa console error atacaktır lütfen kontrol edin
            // eslint-disable-next-line no-console
            console.error('Duplicate Function Name', method);
          }
          this[method] = service[method].bind(service);
        }
      });
    });
  }

  getAllDashboard(option) {
    return this.axiosIns.get(`${this.serviceConfig.dashboard}${option}`);
  }

  editPricing(regionId, args) {
    return this.axiosIns.put(`${this.serviceConfig.pricing}/${regionId}`, args);
  }

  deleteRegionPricing(id) {
    return this.axiosIns.delete(`${this.serviceConfig.pricing}/${id}`);
  }

  getAllPricing(regionId) {
    return this.axiosIns.get(`${this.serviceConfig.regions}/${regionId}/pricing`);
  }

  getPricing(regionId) {
    return this.axiosIns.get(`${this.serviceConfig.pricing}/${regionId}`);
  }

  getCountryList() {
    return this.axiosIns.get('/api/v1/countries');
  }

  getCurrencyList() {
    return this.axiosIns.get('/api/v1/currencies');
  }

  getPreferences() {
    return this.axiosIns.get('/api/v1/constant/Consignment/ConsignmentPreference');
  }

  getSpecialServicesConsignmentsCreate(providerId) {
    return this.axiosIns.get(`/api/v1/providers/${providerId}/feature/special-services`);
  }

  getStateList(countryId) {
    return this.axiosIns.get(`/api/v1/countries/${countryId}/states`);
  }

  getCityList(countryId, cityId) {
    return this.axiosIns.get(`/api/v1/countries/${countryId}/states/${cityId}/cities`);
  }

  downloadDocument(args) {
    const accessToken = this.authService.getToken();
    return axios.get(
      `${process.env.VUE_APP_API_URL}/api/v1/consignments/${args.consignmentId}/documents/${args.documentId}/download`,
      {
        headers: {
          Authorization: `${this.serviceConfig.tokenType} ${accessToken}`
        },
        responseType: 'blob'
      }
    );
  }

  downloadConsignmentDocument(documentId) {
    const accessToken = this.authService.getToken();
    return axios.get(
      `${process.env.VUE_APP_API_URL}/api/v1/consignment-documents/${documentId}/download`,
      {
        headers: {
          Authorization: `${this.serviceConfig.tokenType} ${accessToken}`
        },
        responseType: 'blob'
      }
    );
  }

  downloadLabelDocument(url) {
    const accessToken = this.authService.getToken();
    return axios.get(url, {
      headers: {
        Authorization: `${this.serviceConfig.tokenType} ${accessToken}`
      },
      responseType: 'blob'
    });
  }

  uploadConsignmentDocument(args) {
    return this.axiosIns.post(`${this.serviceConfig.consignmentDocument}`, args);
  }

  getAllNotifications(args) {
    return this.axiosIns.get(this.serviceConfig.notification, { params: args });
  }

  getAllUnreadNotifications() {
    return this.axiosIns.get(`${this.serviceConfig.notification}/unread`);
  }

  adminExportConsignment(args) {
    return this.axiosIns.post(`${this.serviceConfig.adminExportsConsignment}`, args);
  }

  customerExportConsignment(args) {
    return this.axiosIns.post(`${this.serviceConfig.customerExportsConsignment}`, args);
  }

  adminExportConsignmentReport(args) {
    return this.axiosIns.post(`${this.serviceConfig.adminExportsConsignmentReport}`, args);
  }

  readAllNotifications() {
    return this.axiosIns.patch(`${this.serviceConfig.notification}/read`);
  }

  documentExportStart(args) {
    return this.axiosIns.post(`${this.serviceConfig.exportsEndpoint}`, args);
  }

  documentExportControl(id) {
    return this.axiosIns.get(`${this.serviceConfig.exportsEndpoint}/${id}`);
  }

  documentExportDownload(id) {
    const accessToken = this.authService.getToken();
    return this.axiosIns.get(`${this.serviceConfig.exportsEndpoint}/${id}/download`, {
      headers: {
        Authorization: `${this.serviceConfig.tokenType} ${accessToken}`
      },
      responseType: 'blob'
    });
  }

  documentImportStart(args) {
    return this.axiosIns.post(`${this.serviceConfig.billCalculations}/upload-consignments`, args);
  }

  documentImportProgress(id) {
    return this.axiosIns.get(`${this.serviceConfig.billCalculations}/upload-batches/${id}`);
  }

  getRoles(args) {
    return this.axiosIns.get(`${this.serviceConfig.roles}`, { params: args });
  }

  createRoles(args) {
    return this.axiosIns.post(`${this.serviceConfig.roles}`, args);
  }

  getRole(id) {
    return this.axiosIns.get(`${this.serviceConfig.roles}/${id}`);
  }

  updateRole(id, args) {
    return this.axiosIns.put(`${this.serviceConfig.roles}/${id}`, args);
  }

  deleteRoles(id) {
    return this.axiosIns.delete(`${this.serviceConfig.roles}/${id}`);
  }

  getRolePermissions(id) {
    return this.axiosIns.get(`${this.serviceConfig.roles}/${id}/permissions`);
  }

  deletePermission(permissionsId, id) {
    return this.axiosIns.delete(`${this.serviceConfig.roles}/${id}/permissions/${permissionsId}`);
  }

  getPermissions(args) {
    return this.axiosIns.get(`${this.serviceConfig.permissions}`, { params: args });
  }

  addRolePermissions(roleId, permissionsData) {
    return this.axiosIns.post(`${this.serviceConfig.roles}/${roleId}/permissions`, permissionsData);
  }

  getTranslateToEnglish(requestData) {
    return this.axiosIns.post(`${this.serviceConfig.translations}`, requestData);
  }

  getDashboard(args) {
    return this.axiosIns.get(`${this.serviceConfig.dashboard}`, {
      params: args
    });
  }

  getRegionModelType() {
    return this.axiosIns.get('/api/v1/constant/Provider/RegionModelType');
  }

  getExportList(args) {
    return this.axiosIns.get(`${this.serviceConfig.exportsEndpoint}`, { params: args });
  }

  getMetabaseIframeUrl(dashboardId) {
    return this.axiosIns.post(`/api/v1/admin/metabase/dashboards/${dashboardId}/iframe-url`);
  }
}
